/**
 * Parcours « hors campagne » d'une organisation — portail admin.
 *
 * Cible de la pseudo-ligne de `CampagnesOrganisation`. Existe parce que la
 * navigation passe désormais par les campagnes : sans cette vue, les parcours
 * créés sans campagne (données d'avant les campagnes, ou parcours créés à
 * l'unité) ne seraient plus atteignables depuis le portail admin.
 *
 * Sert aussi les parcours sans organisation (`/sans-organisation/hors-campagne`).
 * Deux onglets : les candidats, et les évaluateurs sans campagne.
 */
import {
  Breadcrumb,
  Button,
  DataTable,
  PageContainer,
  PageHeader,
  useFiltresColonnesUrl,
  useFiltresUrl,
} from "@repo/ui";
import { usePaginatedQuery, useQuery } from "convex/react";
import { useMemo } from "react";
import { useParams } from "react-router";
import { api } from "@convex/_generated/api";
import { arianeOrganisation } from "../../lib/ariane";
import type { Id } from "@convex/_generated/dataModel";
import { LoadMore } from "../../components/LoadMore";
import {
  colonnesEvaluateurs,
  FACETTES_EVALUATEURS,
} from "../campagnes/colonnesEvaluateurs";
import {
  colonnesParcoursAdmin,
  FACETTES_PARCOURS_ADMIN,
  RECHERCHE_PARCOURS_ADMIN,
} from "./colonnesParcoursAdmin";

/** Références stables : recréées à chaque rendu, elles feraient boucler les mémos. */
const CHAMPS_FILTRES = {
  q: { colonne: RECHERCHE_PARCOURS_ADMIN.colonne },
  statut: { colonne: "statut", multi: true },
  lien: { colonne: "lien", multi: true },
  epreuve: { colonne: "epreuves", multi: true },
} as const;
/** Type élargi, pas `as const` : `setOngletUrl` doit accepter les deux onglets. */
const ONGLET_DEFAUT: { onglet: Onglet } = { onglet: "candidats" };
const BARRE_FILTRES_CANDIDATS = {
  recherche: RECHERCHE_PARCOURS_ADMIN,
  facettes: FACETTES_PARCOURS_ADMIN,
};
const BARRE_FILTRES_EVALUATEURS = { facettes: FACETTES_EVALUATEURS };

/** Voir `CampagnesOrganisation` : le tri client porte sur ce qui est chargé. */
const TAILLE_PREMIERE_PAGE = 50;

type Onglet = "candidats" | "evaluateurs";

export function HorsCampagne() {
  // Route sans `:orgId` = `/sans-organisation/hors-campagne`.
  const { orgId } = useParams<{ orgId?: string }>();
  const organisationId = orgId as Id<"organisations"> | undefined;

  // L'onglet vit dans l'URL : coller le lien rouvre le même onglet.
  const [ongletUrl, setOngletUrl] = useFiltresUrl(ONGLET_DEFAUT);
  const onglet = ongletUrl.onglet;
  const [filtres, setFiltres] = useFiltresColonnesUrl(CHAMPS_FILTRES);

  const organisation = useQuery(
    api.superAdmin.obtenirOrganisation,
    organisationId ? { organisationId } : "skip",
  );

  // Chaque onglet ne charge que ses propres données : l'onglet fermé est en
  // « skip », donc aucune lecture inutile de parcours.
  const candidats = usePaginatedQuery(
    api.superAdmin.listerParcoursSuperAdmin,
    onglet === "candidats"
      ? {
          filtreSansCampagne: true,
          filtreOrganisationId: organisationId,
          filtreAucuneOrg: organisationId ? undefined : true,
        }
      : "skip",
    { initialNumItems: TAILLE_PREMIERE_PAGE },
  );

  const evaluateurs = usePaginatedQuery(
    api.superAdmin.listerParcoursEvaluateursSuperAdmin,
    onglet === "evaluateurs"
      ? {
          filtreSansCampagne: true,
          filtreOrganisationId: organisationId,
          filtreAucuneOrg: organisationId ? undefined : true,
        }
      : "skip",
    { initialNumItems: TAILLE_PREMIERE_PAGE },
  );

  const colonnesCandidats = useMemo(() => colonnesParcoursAdmin(), []);
  const colonnesEvals = useMemo(() => colonnesEvaluateurs(), []);

  const nomOrganisation = organisationId
    ? (organisation?.nom ?? "Organisation")
    : "Sans organisation";

  const courant = onglet === "candidats" ? candidats : evaluateurs;

  return (
    <PageContainer>
      <Breadcrumb
        items={[
          ...arianeOrganisation(organisationId, organisation?.nom, "campagnes"),
          { label: "Hors campagne" },
        ]}
      />

      <PageHeader
        titre="Hors campagne"
        sousTitre={`${nomOrganisation} · parcours créés sans campagne`}
      />

      <div className="mb-4 flex items-center gap-2">
        <Button
          variant={onglet === "candidats" ? "default" : "outline"}
          onClick={() => setOngletUrl({ onglet: "candidats" })}
        >
          Candidats
        </Button>
        <Button
          variant={onglet === "evaluateurs" ? "default" : "outline"}
          onClick={() => setOngletUrl({ onglet: "evaluateurs" })}
        >
          Évaluateurs
        </Button>
      </div>

      {onglet === "candidats" ? (
        <DataTable
          colonnes={colonnesCandidats}
          donnees={
            candidats.status === "LoadingFirstPage"
              ? undefined
              : candidats.results
          }
          cleColonnes="hors-campagne-candidats"
          hrefLigne={(ligne) => `/parcours/${ligne._id}`}
          barreFiltres={BARRE_FILTRES_CANDIDATS}
          filtres={filtres}
          onFiltresChange={setFiltres}
          vide={{
            titre: "Aucun parcours hors campagne",
            description: "Tous les parcours sont rattachés à une campagne.",
          }}
          videFiltre={{ titre: "Aucun parcours avec ce statut" }}
        />
      ) : (
        <DataTable
          colonnes={colonnesEvals}
          donnees={
            evaluateurs.status === "LoadingFirstPage"
              ? undefined
              : evaluateurs.results
          }
          cleColonnes="hors-campagne-evaluateurs"
          hrefLigne={(ligne) => `/notations/${ligne._id}`}
          barreFiltres={BARRE_FILTRES_EVALUATEURS}
          filtres={filtres}
          onFiltresChange={setFiltres}
          vide={{
            titre: "Aucun évaluateur hors campagne",
            description: "Tous les parcours évaluateurs sont rattachés à une campagne.",
          }}
          videFiltre={{ titre: "Aucun évaluateur avec ces critères" }}
        />
      )}

      <LoadMore
        status={courant.status}
        loadMore={courant.loadMore}
        count={courant.results.length}
      />
    </PageContainer>
  );
}
